'use client';

// LeftSidebarSourcesList - Display content sources in the left sidebar on desktop
// Shows the list without the fixed bottom summary bar

import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslations } from 'next-intl';
import { useContentSources } from './useContentSources';
import { ContentSourceList } from './ContentSourceList';
import { ContentLimitWarning } from './ContentLimitWarning';
import type { ContentSource } from '../../types/content-source';

export interface LeftSidebarSourcesListProps {
  /** Bot ID to fetch content for */
  botId: string | null;
  /** Callback when edit is clicked on a text content source */
  onEditText?: ((contentId: string, content: string) => void) | undefined;
  /** Callback when a content source is deleted (for syncing with local hooks) */
  onDelete?: (contentId: string) => void;
  /** Whether interactions are disabled */
  disabled?: boolean;
  /** Whether this is a session recovery (returning user) */
  isSessionRecovery?: boolean;
  /** Optimistic text entry to show immediately while saving */
  optimisticText?: { content: string; characterCount: number } | null;
  /** Whether text content is currently being saved */
  isSaving?: boolean;
}

/**
 * LeftSidebarSourcesList - Display content sources in a scrollable left sidebar
 *
 * This is a simplified version of UnifiedContentPanel designed for the left sidebar.
 * It shows only the sources list without the fixed bottom summary bar.
 */
export function LeftSidebarSourcesList({
  botId,
  onEditText,
  onDelete,
  disabled = false,
  isSessionRecovery = false,
  optimisticText,
  isSaving = false,
}: LeftSidebarSourcesListProps) {
  const t = useTranslations('creator.contentSources');

  const {
    sources,
    isLoading,
    totalCharacters,
    deleteSource,
    reorderSources,
    isDeleting,
    isReordering,
  } = useContentSources({
    botId,
    enabled: !!botId,
  });

  // Merge optimistic text entry into display sources
  const displaySources = useMemo(() => {
    if (!optimisticText) return sources;

    const optimisticSource: ContentSource = {
      id: 'optimistic-text',
      botId: botId ?? 'pending',
      type: 'text',
      title: optimisticText.content.substring(0, 50) + (optimisticText.content.length > 50 ? '...' : ''),
      content: optimisticText.content,
      characterCount: optimisticText.characterCount,
      status: 'processing',
      displayOrder: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Prepend optimistic entry, filtering out any existing text sources
    // that might overlap (to avoid showing both optimistic and real)
    return [optimisticSource, ...sources];
  }, [sources, optimisticText, botId]);

  // Don't render if nothing to show
  if (!optimisticText && (!botId || (sources.length === 0 && !isLoading))) {
    return null;
  }

  const isInteractionDisabled = disabled || isDeleting || isReordering;

  // Wrapper for delete that also notifies parent for local state sync
  const handleDelete = async (contentId: string) => {
    await deleteSource(contentId);
    onDelete?.(contentId);
  };

  return (
    <>
      {/* Saving indicator */}
      {isSaving && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, px: 1 }}>
          <CircularProgress size={12} />
          <Typography variant="caption" color="text.secondary">
            {t('saving')}
          </Typography>
        </Box>
      )}

      {/* Show loading state */}
      {isLoading && !optimisticText ? (
        <Box sx={{ py: 2 }}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {t('loading')}
          </Typography>
        </Box>
      ) : (
        <>
          {/* Content limit warning */}
          <ContentLimitWarning
            totalCharacters={totalCharacters}
            sources={sources}
            onDelete={handleDelete}
          />

          {/* Content source list with drag-and-drop */}
          <ContentSourceList
            sources={displaySources}
            onReorder={reorderSources}
            onDelete={handleDelete}
            onEdit={onEditText}
            disabled={isInteractionDisabled}
            showEmptyState={false}
            inOverflowContainer
          />
        </>
      )}
    </>
  );
}

export default LeftSidebarSourcesList;
